import { LogToConsole, slogi } from '@web/lib/slog'
import {
  type AnalyticsEventProperties,
  type AnalyticsTrackOptions,
  trackEvent,
} from '@web/o11y/analytics'
import React from 'react'

declare const TRACKING_DESCRIPTOR_BRAND: unique symbol

export type TrackingDescriptor = string & {
  readonly [TRACKING_DESCRIPTOR_BRAND]: true
}

/**
 * Brand a string as a TrackingDescriptor. Intended to be used to define a constant
 * that is used as a tracking descriptor, so that it can be type-checked when passed
 * to tracking functions.
 *
 * @example
 * const MY_TRACKING_DESCRIPTOR = defineTrackingDescriptor('MyTrackingDescriptor')
 *
 * trackContextCtaClicked({
 *   trackingContext: 'MyComponent',
 *   text: 'Click me',
 *   trackingDescriptor: MY_TRACKING_DESCRIPTOR,
 * })
 */
export function defineTrackingDescriptor(value: string): TrackingDescriptor {
  return value as TrackingDescriptor
}

/**
 * Brand a set of strings as TrackingDescriptors.
 *
 * @example
 * const TrackingDescriptors = defineTrackingDescriptors({
 *   CTA_ONE: 'CTA One',
 *   CTA_TWO: 'CTA Two',
 * })
 *
 * trackContextCtaClicked({
 *   trackingContext: 'MyComponent',
 *   text: 'Click me',
 *   trackingDescriptor: TrackingDescriptors.CTA_ONE,
 * })
 */
export function defineTrackingDescriptors<const T extends Record<string, string>>(
  descriptors: T,
): {
  readonly [K in keyof T]: T[K] & TrackingDescriptor
} {
  return descriptors as {
    readonly [K in keyof T]: T[K] & TrackingDescriptor
  }
}

export interface TrackingProperties {
  /** Special property to aid in differentiating as innerText is often translated */
  trackingDescriptor?: TrackingDescriptor
  [key: string]: string | number | boolean | null | undefined
}

export enum TrackingTargets {
  ALL, // Default value. Both RCS and Analytics get events
  NONE, // No events logged. Use this mode to pass trackingProperties through to child events (e.g. CTA Clicks) without other tracking
  // ANALYTICS can't be targeted as the only trackingTarget, because all Analytics events automatically get passed through to RCS
  // To publish a view event to RCS only, use RcsTrackingContext (shared/curator/rcs_tracking_context) instead
}

export interface TrackingContextProps {
  name: string
  properties?: TrackingProperties
  trackingTargets?: TrackingTargets
  enabled?: boolean
  children?: React.ReactNode
  /**
   * This parameter allows developers to ignore the parent context when
   * generating the context name. This is useful when the parent context is
   * not relevant, such as for Curator overlays that are rendered in a React Portal.
   */
  ignoreParent?: boolean
  trackingOptions?: AnalyticsTrackOptions
  /**
   * When true, neither changes to tracking properties, nor toggling 'enabled', will fire a second tracking event.
   * NOTE: remounting the component will always emit a new event.
   */
  onlyOnce?: boolean
}

export interface ClickableTracking {
  /** Properties included in analytics track event. */
  trackingProperties?: TrackingProperties

  /**
   * If provided, will set the `text` property for tracking. If no child is provided, it will be set as the
   * child of the clickable. This allows us to include both the track string and button text in a DRY way.
   */
  innerText?: string
  trackingEventName?: string
  trackingOptions?: AnalyticsTrackOptions
}

export interface ContextCtaClickedProperties extends AnalyticsEventProperties {
  /** The tracking context hierarchy path (eg. 'FileBrowser > ProPitchTile'). */
  trackingContext?: string

  // Text of the CTA.
  text?: string
}

export const DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME = 'CTA Clicked'

/**
 * Tracks a click event.
 *
 * (If using the default context CTA clicked event) To forward event to Datadog:
 * * Provide `trackingDescriptor` in properties
 * * Add the trackingDescriptor to validAdditionalTags in the
 *   `web_events.cta_clicked` entry (registry.go)
 */
export function trackContextCtaClicked(
  properties: ContextCtaClickedProperties,
  eventName?: string,
  sendAsBeacon?: boolean,
  trackingOptions?: AnalyticsTrackOptions,
): void {
  const name = eventName || DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME
  slogi(name, properties.text || '', properties, { logToConsole: LogToConsole.NEVER })

  const newProps = { ...properties, nonInteraction: 0 }
  trackEvent(name, newProps, {
    forwardToDatadog: true,
    sendAsBeacon,
    ...trackingOptions,
  })
}

/** Level of population of an input. It may be in several possible different states. */
export enum InputPopulationLevel {
  // No content.
  NOT_POPULATED = 'not_populated',

  /** Content in the input but is not submittable (eg. incorrectly formatted data). */
  POPULATED_INCOMPLETE = 'populated_incomplete',

  /** Content in the input and it is submittable. */
  POPULATED_COMPLETE = 'populated_complete',
}

export interface ContextInputBlurredProperties extends AnalyticsEventProperties {
  /** The tracking context hierarchy path (eg. 'FileBrowser > ProPitchTile'). */
  trackingContext?: string

  /** Name of the blurred element */
  fieldName: string

  /** True is the input value is populated */
  populationLevel: InputPopulationLevel
}

export function trackContextInputBlurred(
  properties: ContextInputBlurredProperties,
  options?: AnalyticsTrackOptions,
): void {
  const newProps = { ...properties, nonInteraction: 0 }
  trackEvent('Input Blurred', newProps, options)
}

export interface ContextViewProperties extends AnalyticsEventProperties {
  /** Name for the tracking context that was viewed. */
  name: string
}

export function trackContextView(
  properties: ContextViewProperties,
  options: AnalyticsTrackOptions = {},
): void {
  trackEvent('Context Viewed', properties, { forwardToDatadog: true, ...options })
}

export type UseTrackEventFn = (
  // The event name should not have any periods in it since it's used to specify domain and schema.
  event: string,
  params?: AnalyticsEventProperties,
  options?: AnalyticsTrackOptions,
) => void
